// Registered Auditor credential capture + status (Gate 1). KaiLux does NOT verify
// credentials: a person declares their own standing, agrees to a recorded statement
// and renews it yearly (it lapses automatically). The auditor carries the responsibility.
import { useEffect, useState } from 'react'
import { submitAuditCredential, getAuditAttestationStatement, renewAuditCredential, withdrawAuditCredential } from '../../api/kailux'
import { T } from '../../theme/theme'
import { CREDENTIAL_TYPES, credentialTypeLabel, STATUS_LABEL, auditErrorText, formatDate, daysUntil } from '../../utils/audit'
import { Card, Field, Pill, Notice, btn, inputStyle, toneForStatus } from './ui'

const BLANK = { credential_type: 'irba_registered_auditor', full_name: '', registration_number: '', professional_body: '',
  audit_firm_name: '', audit_firm_registration_no: '', attested: false }

export default function CredentialsPanel({ overview, onChanged }) {
  const [form, setForm] = useState(BLANK)
  const [files, setFiles] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState('')
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const type = CREDENTIAL_TYPES.find((t) => t.value === form.credential_type)
  const [statement, setStatement] = useState('')
  useEffect(() => {
    let live = true
    getAuditAttestationStatement(form.credential_type, form.registration_number.trim())
      .then((r) => { if (live) setStatement(r.statement || '') }).catch(() => { if (live) setStatement('') })
    return () => { live = false }
  }, [form.credential_type, form.registration_number])

  const act = async (fn, okText, failText) => {
    setError(''); setDone(''); setBusy(true)
    try { await fn(); setDone(okText); onChanged && onChanged() }
    catch (err) { setError(auditErrorText(err, failText)) } finally { setBusy(false) }
  }

  const submit = async (e) => {
    e.preventDefault()
    setError(''); setDone('')
    setBusy(true)
    try {
      await submitAuditCredential(form, files)
      setDone('Recorded. This is your own declaration; KaiLux does not verify it. Renew it within a year.')
      setForm(BLANK); setFiles([])
      onChanged && onChanged()
    } catch (err) {
      setError(auditErrorText(err, 'Could not submit the credential.'))
    } finally { setBusy(false) }
  }

  const creds = overview?.credentials || []
  return (
    <Card testId="credentials-card" title="Registered Auditor credentials"
      subtitle="An audit report can only be signed by a Registered Auditor with a current declaration. Each person declares their own standing and is responsible for it. KaiLux does not verify it.">
      {creds.length > 0 && (
        <div data-testid="credential-list" style={{ marginBottom: '16px' }}>
          {creds.map((c) => {
            const d = daysUntil(c.next_review_at)
            return (
              <div key={c.id} data-testid={`credential-${c.id}`} style={{ display: 'flex', gap: '12px', alignItems: 'center',
                padding: '10px 0', borderBottom: `1px solid ${T.bd}`, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: T.tx }}>{c.full_name}</div>
                  <div style={{ fontSize: '12px', color: T.mt }}>{credentialTypeLabel(c.credential_type)}</div>
                </div>
                <Pill tone={toneForStatus(c.status)}>{STATUS_LABEL[c.status] || c.status}</Pill>
                {c.status === 'attested' && (
                  <span style={{ fontSize: '12px', color: d !== null && d < 30 ? T.am : T.mt }}>
                    Declared by the holder; renew by {formatDate(c.next_review_at)}{d !== null && d < 0 ? ' (lapsed)' : ''}
                  </span>
                )}
                {c.status === 'attested' && c.firm_user_id === overview?.my_firm_user_id && (
                  <>
                    <button type="button" disabled={busy} style={btn(true, busy)} aria-label={`Renew declaration for ${c.full_name}`}
                      onClick={() => act(() => renewAuditCredential(c.id), 'Renewed for another year.', 'Could not renew.')}>Renew</button>
                    <button type="button" disabled={busy} style={btn(false, busy)} aria-label={`Withdraw declaration for ${c.full_name}`}
                      onClick={() => act(() => withdrawAuditCredential(c.id), 'Withdrawn.', 'Could not withdraw.')}>Withdraw</button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
      {!overview?.has_valid_registered_auditor && (
        <Notice tone="warn" testId="no-valid-ra">
          This firm has no Registered Auditor with a current declaration right now, so audit engagements cannot be opened.
        </Notice>
      )}
      <form onSubmit={submit} data-testid="credential-form">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '12px' }}>
          <Field label="Credential type">
            <select aria-label="Credential type" value={form.credential_type} onChange={set('credential_type')} style={inputStyle}>
              {CREDENTIAL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Full name (as registered)">
            <input aria-label="Full name" required value={form.full_name} onChange={set('full_name')} style={inputStyle} />
          </Field>
          <Field label="Registration / membership number">
            <input aria-label="Registration number" required value={form.registration_number} onChange={set('registration_number')} style={inputStyle} />
          </Field>
          <Field label="Professional body" hint="e.g. IRBA, SAICA, SAIPA">
            <input aria-label="Professional body" value={form.professional_body} onChange={set('professional_body')} style={inputStyle} />
          </Field>
          {type?.needsFirm && (
            <>
              <Field label="Registered audit firm name">
                <input aria-label="Audit firm name" required value={form.audit_firm_name} onChange={set('audit_firm_name')} style={inputStyle} />
              </Field>
              <Field label="Audit firm registration number">
                <input aria-label="Audit firm registration number" required value={form.audit_firm_registration_no}
                  onChange={set('audit_firm_registration_no')} style={inputStyle} />
              </Field>
            </>
          )}
        </div>
        <div style={{ marginTop: '12px' }}>
          <Field label="Supporting documents (optional; PDF, PNG or JPG, up to 5 files)" hint="Kept on your record for your own reference. KaiLux does not check them.">
            <input aria-label="Evidence files" type="file" multiple accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => setFiles(Array.from(e.target.files || []))} style={{ ...inputStyle, padding: '7px' }} />
          </Field>
        </div>
        <div style={{ marginTop: '12px' }}>
          <div data-testid="attestation-statement" style={{ fontSize: '13px', color: T.tx, border: `1px solid ${T.bd}`, borderRadius: '8px', padding: '10px 12px' }}>{statement}</div>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginTop: '10px', fontSize: '13px', color: T.tx }}>
            <input type="checkbox" aria-label="I make this declaration" checked={form.attested}
              onChange={(e) => setForm((f) => ({ ...f, attested: e.target.checked }))} />
            <span>I make this declaration and accept that I am responsible for it.</span>
          </label>
        </div>
        {error && <div style={{ marginTop: '12px' }}><Notice tone="bad" testId="credential-error">{error}</Notice></div>}
        {done && <div style={{ marginTop: '12px' }}><Notice tone="ok" testId="credential-done">{done}</Notice></div>}
        <div style={{ marginTop: '14px' }}>
          <button type="submit" disabled={busy || !form.attested} style={btn(true, busy || !form.attested)}>{busy ? 'Saving…' : 'Record my declaration'}</button>
        </div>
      </form>
    </Card>
  )
}
